import React, { useState } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Button,
  Chip,
  Card,
  CardContent,
  Divider,
  Link as MuiLink,
  Grid
} from "@mui/material";
import {
  Logout,
  Language,
  Twitter,
  Telegram,
  AccountBalanceWallet,
  Refresh,
  Dashboard as DashboardIcon,
  Apps as ProjectsIcon,
  Menu as MenuIcon
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { useQuery } from "@tanstack/react-query";
import { currentProjectState } from "../state/atoms";
import { adminAPI } from "../api/admin";
import TrainingManager from "../components/TrainingManager";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const project = useRecoilValue(currentProjectState);
  const [isRetraining, setIsRetraining] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { data: stats, refetch: refetchStats } = useQuery({
    queryKey: ["dashboardStats"],
    queryFn: () => adminAPI.getStats()
  });

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/login");
  };

  const handleRetrain = async () => {
    setIsRetraining(true);
    try {
      await adminAPI.retrain();
      await refetchStats();
    } catch (error) {
      console.error("Error retraining:", error);
    } finally {
      setIsRetraining(false);
    }
  };

  if (!project) {
    return null;
  }

  return (
    <Box sx={{ flexGrow: 1, minHeight: "100vh" }}>
      {/* App Bar */}
      <AppBar
        position="static"
        elevation={0}
      >
        <Toolbar>
          <Box
            component="img"
            src="/assets/images/logoWithWhiteText.png"
            alt="WebPal logo"
            sx={{
              height: { xs: 32, sm: 40 },
              marginRight: "auto",
              cursor: "pointer",
              filter: "drop-shadow(0 2px 8px rgba(255, 237, 0, 0.2))",
              transition: "all 0.3s ease",
              "&:hover": {
                filter: "drop-shadow(0 4px 12px rgba(255, 237, 0, 0.3))",
                transform: "scale(1.02)"
              }
            }}
          />
          {/* Mobile Menu Button */}
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            sx={{
              margin: 0,
              padding: 0,
              display: { xs: "block", sm: "none" }
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Desktop Navigation */}
          <Box
            sx={{
              display: { xs: "none", sm: "flex" },
              gap: 1,
              alignItems: "center"
            }}
          >
            {/* Super User Navigation */}
            {project.is_super_project && (
              <>
                <Button
                  startIcon={<DashboardIcon />}
                  variant="outlined"
                  size="small"
                  onClick={() => navigate("/dashboard")}
                  sx={{
                    fontSize: "0.875rem",
                    px: 2
                  }}
                >
                  Dashboard
                </Button>
                <Button
                  startIcon={<ProjectsIcon />}
                  variant="outlined"
                  size="small"
                  onClick={() => navigate("/projects")}
                  sx={{
                    borderColor: "#FFED00",
                    color: "#FFED00",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    px: 2,
                    "&:hover": {
                      borderColor: "#FFF27A",
                      background: "rgba(255, 237, 0, 0.1)"
                    }
                  }}
                >
                  Projects
                </Button>
              </>
            )}

            <IconButton onClick={handleLogout} color="inherit" size="small">
              <Logout />
            </IconButton>
          </Box>
        </Toolbar>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <Box
            sx={{
              display: { xs: "block", sm: "none" },
              py: 2,
              px: 2
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1
              }}
            >
              {/* Super User Navigation */}
              {project.is_super_project && (
                <>
                  <Button
                    startIcon={<DashboardIcon />}
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      navigate("/dashboard");
                      setMobileMenuOpen(false);
                    }}
                    sx={{
                      borderColor: "#3D474E",
                      color: "#e8ecf1",
                      fontSize: "0.875rem",
                      px: 2,
                      "&:hover": {
                        borderColor: "#FFED00",
                        background: "rgba(255, 237, 0, 0.1)",
                        color: "#FFED00"
                      }
                    }}
                  >
                    Dashboard
                  </Button>
                  <Button
                    startIcon={<ProjectsIcon />}
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      navigate("/projects");
                      setMobileMenuOpen(false);
                    }}
                    sx={{
                      borderColor: "#3D474E",
                      color: "#e8ecf1",
                      fontSize: "0.875rem",
                      px: 2,
                      "&:hover": {
                        borderColor: "#FFED00",
                        background: "rgba(255, 237, 0, 0.1)",
                        color: "#FFED00"
                      }
                    }}
                  >
                    Projects
                  </Button>
                </>
              )}

              {/* <Button
                startIcon={<Refresh />}
                variant="outlined"
                size="small"
                onClick={() => {
                  handleRetrain();
                  setMobileMenuOpen(false);
                }}
                disabled={isRetraining}
                sx={{
                  fontSize: "0.875rem",
                  px: 2
                }}
              >
                {isRetraining ? "Retraining..." : "Retrain AI"}
              </Button> */}

              <Button
                startIcon={<Logout />}
                variant="outlined"
                size="small"
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                sx={{
                  fontSize: "0.875rem",
                  px: 2,
                  borderColor: "#3D474E",
                  color: "#e8ecf1",
                  "&:hover": {
                    borderColor: "#FFED00",
                    background: "rgba(255, 237, 0, 0.1)",
                    color: "#FFED00"
                  }
                }}
              >
                Logout
              </Button>
            </Box>
          </Box>
        )}
      </AppBar>

      <Container
        maxWidth="xl"
        sx={{
          mt: { xs: 2, sm: 4 },
          pb: { xs: 2, sm: 4 },
          px: { xs: 2, sm: 3 }
        }}
      >
        <Grid container spacing={3}>
          {/* Project Profile Section */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: "100%" }}>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Avatar
                  src={project.profile_logo_s3_url}
                  sx={{
                    width: 100,
                    height: 100,
                    margin: "0 auto",
                    bgcolor: "primary.main",
                    fontSize: "2.5rem"
                  }}
                >
                  {!project.profile_logo_s3_url && project.name
                    ? project.name.charAt(0).toUpperCase()
                    : ""}
                </Avatar>
                <Typography variant="h5" sx={{ mt: 2, fontWeight: "bold" }}>
                  {project.name}
                </Typography>
                <Chip
                  label={project.unique_code}
                  size="small"
                  sx={{ mt: 1 }}
                  color="primary"
                  variant="outlined"
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Project Details
                </Typography>

                {project.website && (
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Language
                      fontSize="small"
                      sx={{ mr: 1, color: "text.secondary" }}
                    />
                    <MuiLink
                      href={project.website}
                      target="_blank"
                      rel="noopener"
                      underline="hover"
                      sx={{ color: "#e8ecf1", "&:hover": { color: "#FFED00" } }}
                    >
                      Website
                    </MuiLink>
                  </Box>
                )}

                {project.twitter && (
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Twitter
                      fontSize="small"
                      sx={{ mr: 1, color: "text.secondary" }}
                    />
                    <MuiLink
                      href={project.twitter}
                      target="_blank"
                      rel="noopener"
                      underline="hover"
                      sx={{ color: "#e8ecf1", "&:hover": { color: "#FFED00" } }}
                    >
                      Twitter
                    </MuiLink>
                  </Box>
                )}

                {project.telegram && (
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Telegram
                      fontSize="small"
                      sx={{ mr: 1, color: "text.secondary" }}
                    />
                    <MuiLink
                      href={project.telegram}
                      target="_blank"
                      rel="noopener"
                      underline="hover"
                      sx={{ color: "#e8ecf1", "&:hover": { color: "#FFED00" } }}
                    >
                      Telegram
                    </MuiLink>
                  </Box>
                )}

                {project.contract_address && (
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <AccountBalanceWallet
                      fontSize="small"
                      sx={{ mr: 1, color: "text.secondary" }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                    >
                      {project.contract_address.slice(0, 8)}...
                      {project.contract_address.slice(-6)}
                    </Typography>
                  </Box>
                )}

                {project.chain && (
                  <Chip label={project.chain} size="small" sx={{ mt: 1 }} />
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Features Enabled
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                  {project.features.chat && (
                    <Chip label="Chat" size="small" color="success" />
                  )}
                  {project.features.ai_search && (
                    <Chip label="AI Web3 Search" size="small" color="info" />
                  )}
                  {project.features.feedback && (
                    <Chip label="Feedback" size="small" color="warning" />
                  )}
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Stats and Training Section */}
          <Grid item xs={12} md={8}>
            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography
                      color="text.secondary"
                      gutterBottom
                      variant="body2"
                    >
                      Training Sets
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {stats?.trainingSetCount || 0}
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        /100
                      </Typography>
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography
                      color="text.secondary"
                      gutterBottom
                      variant="body2"
                    >
                      AI Status
                    </Typography>
                    <Chip
                      label={stats?.isVectorTrained ? "Trained" : "Not Trained"}
                      color={stats?.isVectorTrained ? "success" : "default"}
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography
                      color="text.secondary"
                      gutterBottom
                      variant="body2"
                    >
                      Paragraphs
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {stats?.paragraphCount || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Training Manager */}
            <Paper
              elevation={2}
              sx={{
                p: {
                  xs: 1,
                  sm: 2
                },
                borderRadius: 2
              }}
            >
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Training Data Management
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Add, edit, and manage your Q&A training sets. The AI will
                automatically retrain when you make changes.
              </Typography>
              <TrainingManager onUpdate={refetchStats} />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Dashboard;
